import React from 'react';
import { OvernightCityPageData } from 'types/app';
import { HelmetProps } from '../../components/Helmet';
import { ENDPOINT_CITY_OVERNIGHT_ARTICLE_GET, App, BASE_URL, ProviderServiceType, Routes, HttpHeaderKey, CityPages, ServiceLandingDownloadLinks } from '../../constants';
import { DEFAULT_CACHE_POLICY } from '../../constants/cache';
import { getCityBoardingServiceFaq } from '../../data';
import { GetWalkingCityArticleResponse, getWalkingCityArticleResponseTransformer, ResponseProfileReview } from '../../transformers';
import { axiosContent } from '../../utils/axios';
import { NotFoundException } from '../../utils/exceptions/NotFoundException';
import { breadcrumbsBuilder } from '../../utils/breadcrumbsBuilder';
import { formatter } from '../../utils/formatter';
import { locationParser } from '../../utils/formatter/parsers/locationParser';
import { breadcrumbsSchemaBuilder } from '../../utils/breadcrumbsSchemaBuilder';
import { requestHandler } from '../../utils/requestHandler';
import { createWalkerProfileUrl } from '../../utils/createWalkerProfileUrl';
import { createSchemaItemList } from '../../seo-schemas/itemList';
import { createSchemaProduct } from '../../seo-schemas/product';
import { CityPageContainer } from '../../containers/CityPageContainer';
import { getCityWalkers } from '../../hooks/api/useGetCityWalkers';
import { createSchemaDownloadAction, createSchemaPerson, createSchemaService } from '../../seo-schemas';
import { getCityReviews } from '../../hooks/api/useGetCityReviews';
import { ServiceDropdownOptionType } from '../../containers/CityPageContainer/components/ServiceFilter/components/ServiceDropdown';
import { Breadcrumb } from '../../wag-react/components/Breadcrumbs';
import { KustomerChatScript } from '@/wag-react/components/kustomer-chat/KustomerChatScript';
type Props = {
  data: OvernightCityPageData;
  locationSlug: string;
  breadcrumbItems: Breadcrumb[];
  meta: Pick<HelmetProps, 'title' | 'description'>;
  queryParams: Record<string, string>;
  structuredData: {
    // The package `schema-dts` causes us some memory heap issues
    // https://github.com/google/schema-dts/issues/34
    // We will now be removing it and using the `unknown` type
    // Documentation here https://blog.logrocket.com/when-to-use-never-and-unknown-in-typescript-5e4d6c5799ad/
    // TODO - We should type this properly something like `schema-dts`
    breadcrumbs: unknown;
    downloadAction: unknown;
    video: unknown;
    faq: unknown;
    carousel: unknown;
    product: unknown;
    services: unknown[];
  };
};
const locationFormatter = formatter({
  parser: locationParser
});
const parseSlug = (location: string) => {
  const STATE_CITY_REGEX = /^(?<state>[a-zA-Z]{2})-(?<city>[\w].+)$/;
  const match = STATE_CITY_REGEX.exec(location);
  if (!match) {
    // No regex match, so there's nothing to return
    return {};
  }
  const {
    groups: {
      state,
      city
    }
  } = match;
  return {
    state,
    city
  };
};
const CityOvernightPage = ({
  data,
  breadcrumbItems,
  meta,
  structuredData,
  queryParams
}: Props) => {
  if (!data) {
    return null;
  }
  const {
    city,
    state,
    ...restData
  } = data;
  return <>
      <CityPageContainer service={ProviderServiceType.Boarding} breadcrumbItems={breadcrumbItems} data={{
      city,
      state,
      ...restData
    }} meta={meta} structuredData={structuredData} queryParams={queryParams} data-sentry-element="CityPageContainer" data-sentry-source-file="[location].tsx" />
      <KustomerChatScript data-sentry-element="KustomerChatScript" data-sentry-source-file="[location].tsx" />
    </>;
};
const handleGetServerSideProps = requestHandler({
  headers: {
    /**
     * This page hits WagApi for walker profiles and since
     * we aren't busting the cache on WagApi yet, it won't purge the cache.
     * https://github.com/waglabs/wagweb/pull/596#discussion_r522358536
     */
    [HttpHeaderKey.CacheControl]: DEFAULT_CACHE_POLICY
  }
});
export const getServerSideProps = handleGetServerSideProps(async ({
  query
}) => {
  try {
    const {
      location: locationSlug,
      ...restQueryParams
    } = query;
    const {
      state,
      city
    } = parseSlug(String(locationSlug));
    if (!state || !city) {
      return {
        notFound: true
      };
    }
    const initialService = restQueryParams?.service?.toString() as ServiceDropdownOptionType;
    const breadcrumbItems = breadcrumbsBuilder().add({
      title: App.Wag,
      url: BASE_URL
    }).add({
      title: 'Dog Boarding',
      url: Routes.DogBoardingArticleDirectory
    }).add({
      title: locationFormatter({
        // TODO - Didn't add this to the locationFormatter
        // because I think this should be handled on the consumer
        // we shouldn't always replace hypen with whitespace because there are other cities that
        // has hypen on its city name
        city: city.replace('-', ' '),
        stateAbbrev: state.toUpperCase()
      })
    }).build({
      includeDefaultHome: false
    });
    const initialProps = {
      locationSlug,
      breadcrumbItems,
      meta: {},
      structuredData: {}
    };
    const getBoardingCityArticleByLocation = async () => {
      const {
        longitude,
        latitude,
        ...rest
      } = (await axiosContent.get<GetWalkingCityArticleResponse>(ENDPOINT_CITY_OVERNIGHT_ARTICLE_GET({
        city,
        state
      }))).data;
      if (!latitude || !longitude) {
        throw NotFoundException('Coordinates not found for city boarding');
      }
      const data = getWalkingCityArticleResponseTransformer({
        longitude,
        latitude,
        ...rest
      });
      return data;
    };
    const article = await getBoardingCityArticleByLocation();
    let [promotedProfiles, notPromotedProfiles] = await Promise.all([getCityWalkers({
      latitude: article.latitude,
      longitude: article.longitude,
      perPage: CityPages.PromotedProfilesPerPage as number,
      promoted: true,
      sortBy: [{
        column: 'random'
      }],
      service: initialService
    }), getCityWalkers({
      latitude: article.latitude,
      longitude: article.longitude,
      perPage: CityPages.OrganicProfilesPerPage as number,
      service: initialService
    })]);
    if (!promotedProfiles.profiles.length && !notPromotedProfiles.profiles.length) {
      [promotedProfiles, notPromotedProfiles] = await Promise.all([getCityWalkers({
        latitude: article.latitude,
        longitude: article.longitude,
        distance: CityPages.MaxWalkerDistance as number,
        perPage: CityPages.PromotedProfilesPerPage as number,
        promoted: true,
        sortBy: [{
          column: 'random'
        }],
        service: initialService
      }), getCityWalkers({
        latitude: article.latitude,
        longitude: article.longitude,
        distance: CityPages.MaxWalkerDistance as number,
        perPage: CityPages.OrganicProfilesPerPage as number,
        service: initialService
      })]);
    }
    const {
      reviews
    } = await getCityReviews({
      latitude: article.latitude,
      longitude: article.longitude,
      perPage: CityPages.OrganicProfilesPerPage as number,
      service: initialService,
      distanceMiles: CityPages.DefaultWalkerDistance as number
    });
    const data = {
      nextPage: promotedProfiles.nextPage || notPromotedProfiles.nextPage,
      prevPage: promotedProfiles.prevPage || notPromotedProfiles.prevPage,
      maxNumberOfPages: Math.max(promotedProfiles.maxNumberOfPages, notPromotedProfiles.maxNumberOfPages),
      profiles: [...promotedProfiles.profiles, ...notPromotedProfiles.profiles],
      reviews,
      ...article
    };
    const meta = {
      title: `Top Dog Sitters and Boarders in ${data.city}, ${data.state} | Wag!`,
      description: `Book trusted dog sitters and boarders in ${data.city}, ${data.state}. Whether it’s a quick trip or a long vacation, Wag! connects pet parents with experienced pet caregivers.`
    };
    const breadcrumbStructuredData = breadcrumbsSchemaBuilder().add({
      id: BASE_URL,
      name: App.Wag
    }).add({
      id: Routes.DogBoardingArticleDirectory,
      name: 'Dog Boarding'
    }).add({
      id: `${Routes.DogBoardingArticleDirectory}/${locationSlug}`,
      name: meta.title
    }).build();
    const {
      structuredData: faqStructuredData
    } = getCityBoardingServiceFaq({
      city: data.city,
      state: data.state
    });
    const carouselStructuredData = createSchemaItemList({
      numberOfItems: data.profiles.length,
      items: data.profiles.map(profile => ({
        name: profile.name,
        url: createWalkerProfileUrl({
          promoCode: profile.promoCode
        })
      }))
    });
    let productStructuredData = null;
    if (data.profiles.length > 0) {
      productStructuredData = createSchemaProduct({
        ratings: data.profiles.filter(profile => !!profile.rating).map(profile => profile.rating),
        ...(reviews.length && {
          firstReview: reviews[0] as ResponseProfileReview
        }),
        location: {
          city: data.city,
          state: data.state
        },
        meta,
        numberOfProfiles: data.profiles.length,
        service: {
          type: ProviderServiceType.Boarding,
          lowPrice: CityPages.DogBoardingMinimumPrice as number,
          highPrice: CityPages.DogBoardingMaximumPrice as number
        }
      });
    }
    const downloadActionStructuredData = createSchemaDownloadAction({
      description: 'Wag deeplink to download app or go to Dog Boarding flow',
      target: ServiceLandingDownloadLinks.Overnight
    });
    const servicesStructuredData = data.profiles.map(({
      bio,
      name,
      rating
    }) => createSchemaService({
      serviceType: 'Dog Boarding',
      name: `Boarding service by ${name}`,
      description: `Boarding service by ${name}`,
      addressLocality: data.city,
      addressRegion: data.state,
      audienceDescription: 'Pet parents who want boarding services for their dog',
      aggregateRating: rating,
      provider: createSchemaPerson({
        name,
        affiliation: 'Independent Contractor',
        description: bio,
        potentialAction: 'Book'
      })
    }));
    return {
      props: {
        ...initialProps,
        queryParams: restQueryParams,
        meta,
        structuredData: {
          breadcrumbs: breadcrumbStructuredData,
          downloadAction: downloadActionStructuredData,
          faq: faqStructuredData,
          carousel: carouselStructuredData,
          product: productStructuredData,
          services: servicesStructuredData,
          ...data.structuredData
        },
        data
      }
    };
  } catch (error) {
    console.error('Error in getServerSideProps:', error);
    return {
      notFound: true
    };
  }
});
export default CityOvernightPage;